'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { Badge, Text, Tooltip } from '@mantine/core';
import ResourceIcon from '@features/Resource/ResourceIcon';
import { ResourceBadgeProps } from './types';
import * as classes from './styles/index.css';
import * as badgeStylesAPI from './styles/badgeStylesAPI.css';
import { memo } from 'react';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

/**
 * A simpler version of the ResourceBadge component, that does not need a fully loaded resource.
 */
export default memo(function ResourceBadgeSimple({
  resource,
  withActions = false,
  withoutTooltip = true,
  withHoverableFilePreview = false,
  truncable = false,
  iconSize,
  fontSize = 12,
  hideLabel,
  onClick,
  cursor = onClick ? 'pointer' : 'default',
  ...props
}: ResourceBadgeProps) {
  /* ********************************** HOOKS ********************************* */

  // Style

  /* ***************************** LOCAL VARIABLES **************************** */

  const icon = (
    <ResourceIcon
      className={classes.icon}
      resource={resource}
      withColor
      size={iconSize}
    />
  );

  /* ******************************** RENDERING ******************************* */

  return (
    <Tooltip
      label={resource.name}
      position="top"
      fz="xs"
      withinPortal
      zIndex={400}
      display={withoutTooltip ? 'none' : undefined}
    >
      <Badge
        classNames={{ ...badgeStylesAPI }}
        size="xs"
        h="auto"
        tt="inherit"
        px={0}
        radius={0}
        leftSection={icon}
        fullWidth
        onClick={onClick}
        {...props}
      >
        {hideLabel ? null : (
          <Text
            fz={fontSize}
            c="blackroomBlack"
            style={{ cursor }}
            maw={truncable ? '95%' : undefined}
            truncate={truncable ? true : undefined}
          >
            {resource.name}
          </Text>
        )}
      </Badge>
    </Tooltip>
  );
});
